import { useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { Button, DataTable, EmptyState, Pill } from '@orion/ui';
import { PageHeader } from '../../app/shell.js';
import { useAuth } from '../../lib/auth.js';
import { SavedViewsBar } from '../saved-views/SavedViewsBar.js';
import { useCompaniesQuery } from './api.js';

const LIFECYCLE_COLOR: Record<string, string> = {
  prospect: '#454A54',
  customer: '#1E7A4C',
  former_customer: '#B42318',
  partner: '#5B34B0',
};

export function CompaniesListPage() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const { data, isLoading } = useCompaniesQuery({ page, pageSize: 25, q: q || undefined });
  const companies = data?.data ?? [];
  const total = data?.meta?.total ?? 0;
  const exportUrl = `/api/v1/companies/export${q ? `?q=${encodeURIComponent(q)}` : ''}`;

  return (
    <>
      <PageHeader
        title="Companies"
        actions={
          <div className="flex items-center gap-2">
            {hasPermission('crm.companies.export') && (
              <a
                href={exportUrl}
                className="inline-flex h-9 items-center rounded-control border border-border-strong bg-surface px-3.5 text-sm font-semibold text-ink hover:bg-surface-subtle"
              >
                Export CSV
              </a>
            )}
            {hasPermission('crm.companies.create') && (
              <>
                <Link
                  to="/prospecting"
                  className="inline-flex h-9 items-center rounded-control border border-border-strong bg-surface px-3.5 text-sm font-semibold text-ink hover:bg-surface-subtle"
                >
                  Find prospects
                </Link>
                <Link to="/companies/new">
                  <Button size="md">Add company</Button>
                </Link>
              </>
            )}
          </div>
        }
      />
      <main className="flex flex-1 flex-col gap-4 p-7">
        <SavedViewsBar
          entityType="company"
          q={q}
          onApplyQuery={(nextQ) => {
            setQ(nextQ);
            setPage(1);
          }}
        />
        <input
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
          type="search"
          placeholder="Search company name…"
          className="h-9 w-full max-w-md rounded-control border border-border-strong bg-surface px-3 text-sm"
        />

        {!isLoading && companies.length === 0 && (
          <EmptyState title="No companies yet" description="Companies are created directly or by converting a lead." />
        )}

        {companies.length > 0 && (
          <DataTable
            rows={companies}
            rowKey={(c) => c.id}
            onRowClick={(c) => navigate({ to: '/companies/$companyId', params: { companyId: c.id } })}
            columns={[
              { key: 'name', header: 'Name', width: '2fr', render: (c) => <span className="font-medium text-ink">{c.name}</span> },
              { key: 'domain', header: 'Domain', width: '1.4fr', render: (c) => c.domain ?? '—' },
              { key: 'industry', header: 'Industry', width: '1.2fr', render: (c) => c.industry ?? '—' },
              {
                key: 'lifecycle',
                header: 'Lifecycle',
                width: '1fr',
                render: (c) => <Pill label={c.lifecycleStage} color={LIFECYCLE_COLOR[c.lifecycleStage]} />,
              },
              { key: 'owner', header: 'Owner', width: '1fr', render: (c) => c.owner?.displayName ?? 'Unassigned' },
            ]}
          />
        )}

        {total > 0 && (
          <div className="flex items-center justify-between text-sm text-text-muted">
            <span>
              {(page - 1) * 25 + 1}–{Math.min(page * 25, total)} of {total}
            </span>
            <div className="flex gap-2">
              <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="rounded-control border border-border-strong px-2.5 py-1 disabled:opacity-40">
                Previous
              </button>
              <button type="button" disabled={page * 25 >= total} onClick={() => setPage((p) => p + 1)} className="rounded-control border border-border-strong px-2.5 py-1 disabled:opacity-40">
                Next
              </button>
            </div>
          </div>
        )}
      </main>
    </>
  );
}
